import { PUBLIC_EMAIL } from './publicContact';

/** How the lead first entered the pipeline */
export const LEAD_SOURCES = [
  { value: 'cold_call', label: 'Cold call list' },
  { value: 'cqc_register', label: 'CQC register research' },
  { value: 'google_maps', label: 'Google / maps search' },
  { value: 'referral', label: 'Referral' },
  { value: 'website_staffing_form', label: 'Staffing web form' },
  { value: 'website_contact', label: 'Website contact form' },
  { value: 'website_live_chat', label: 'Website live chat' },
  { value: 'linkedin', label: 'LinkedIn outreach' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'email', label: 'Inbound email' },
  { value: 'event', label: 'Event / conference' },
  { value: 'in_person', label: 'Met in person' },
  { value: 'other', label: 'Other' },
];

/** Every way you can touch a lead (log + quick-launch) */
export const OUTREACH_CHANNELS = [
  { value: 'call', label: 'Phone call', icon: 'Phone', canLaunch: true, group: 'direct' },
  { value: 'sms', label: 'SMS / text', icon: 'MessageSquare', canLaunch: true, group: 'direct' },
  { value: 'email', label: 'Email', icon: 'Mail', canLaunch: true, group: 'direct' },
  { value: 'whatsapp', label: 'WhatsApp', icon: 'MessageCircle', canLaunch: true, group: 'direct' },
  { value: 'linkedin', label: 'LinkedIn', icon: 'Linkedin', canLaunch: true, group: 'social', copyTemplate: true },
  { value: 'instagram', label: 'Instagram DM', icon: 'Instagram', canLaunch: true, group: 'social', copyTemplate: true },
  { value: 'facebook', label: 'Facebook', icon: 'Facebook', canLaunch: true, group: 'social', copyTemplate: true },
  { value: 'video_call', label: 'Video call', icon: 'Video', canLaunch: true, group: 'field' },
  { value: 'in_person', label: 'Visit in person', icon: 'MapPin', canLaunch: true, group: 'field' },
  { value: 'letter', label: 'Letter / post', icon: 'Send', canLaunch: false, group: 'field' },
  { value: 'live_chat', label: 'Live chat', icon: 'MessagesSquare', canLaunch: false, group: 'field' },
  { value: 'referral_intro', label: 'Referral intro', icon: 'UserPlus', canLaunch: false, group: 'field' },
  { value: 'note', label: 'Internal note', icon: 'StickyNote', canLaunch: false, group: 'internal' },
];

/** Research shortcuts — what top outbound teams open before the first call */
export const RESEARCH_SHORTCUTS = [
  { value: 'cqc', label: 'CQC profile', icon: 'Shield', hint: 'Rating, manager, regulated activities' },
  { value: 'google', label: 'Google search', icon: 'Search', hint: 'Phone, website, reviews' },
  { value: 'maps', label: 'Google Maps', icon: 'MapPin', hint: 'Address & directions for visits' },
  { value: 'website', label: 'Their website', icon: 'Globe', hint: 'Contact form & staff page' },
  { value: 'companies_house', label: 'Companies House', icon: 'Building2', hint: 'Directors & registered office' },
];

export const CHANNEL_GROUPS = [
  { id: 'direct', label: 'Direct contact' },
  { id: 'social', label: 'Social' },
  { id: 'field', label: 'Visit & meetings' },
  { id: 'internal', label: 'Internal' },
];

export const ACTIVITY_OUTCOMES = [
  { value: 'connected', label: 'Spoke to decision-maker' },
  { value: 'gatekeeper', label: 'Spoke to gatekeeper only' },
  { value: 'voicemail', label: 'Left voicemail' },
  { value: 'no_answer', label: 'No answer' },
  { value: 'email_sent', label: 'Email sent' },
  { value: 'message_sent', label: 'Message sent (DM/SMS/WhatsApp)' },
  { value: 'replied', label: 'They replied' },
  { value: 'interested', label: 'Interested — wants info' },
  { value: 'callback_requested', label: 'Asked to call back' },
  { value: 'meeting_booked', label: 'Meeting / demo booked' },
  { value: 'quoted', label: 'Rate / quote discussed' },
  { value: 'not_interested', label: 'Not interested' },
  { value: 'wrong_contact', label: 'Wrong contact / number' },
];

export const LEAD_TEMPERATURES = [
  { value: 'hot', label: 'Hot', color: 'bg-red-100 text-red-700' },
  { value: 'warm', label: 'Warm', color: 'bg-amber-100 text-amber-800' },
  { value: 'cold', label: 'Cold', color: 'bg-sky-100 text-sky-700' },
];

export const PREFERRED_CHANNELS = OUTREACH_CHANNELS.map((c) => ({
  value: c.value,
  label: c.label,
}));

/** Best-practice multi-touch sequence (used by top outbound teams) */
export const OUTREACH_PLAYBOOK = [
  { step: 1, channel: 'call', waitDays: 0, hint: 'Intro call to Registered Manager' },
  { step: 2, channel: 'email', waitDays: 1, hint: 'Follow-up email with staffing one-pager' },
  { step: 3, channel: 'linkedin', waitDays: 2, hint: 'Connect + short note on LinkedIn' },
  { step: 4, channel: 'whatsapp', waitDays: 2, hint: 'WhatsApp if mobile on file' },
  { step: 5, channel: 'call', waitDays: 3, hint: 'Second call — reference prior touches' },
  { step: 6, channel: 'instagram', waitDays: 3, hint: 'DM if they are active on Instagram' },
  { step: 7, channel: 'letter', waitDays: 4, hint: 'Posted intro letter to the home' },
  { step: 8, channel: 'sms', waitDays: 4, hint: 'Brief SMS before final call' },
  { step: 9, channel: 'in_person', waitDays: 5, hint: 'Drop-in visit if local' },
  { step: 10, channel: 'call', waitDays: 5, hint: 'Final attempt before nurture pause' },
];

export const OUTREACH_TEMPLATES = {
  call: 'Introduced Osabea agency staffing, worker suitability checks and care-home cover needs. No individual worker described as CQC registered.',
  email: `Hi {{contact_name}},\n\nI'm reaching out from Osabea Healthcare Solutions Limited. We arrange agency staffing for care homes across Kent, including healthcare assistants, nurses and support workers, subject to availability and role-specific checks.\n\nHomes we work with value:\n• Role-specific worker checks and suitability reviewed before a placement is confirmed\n• Transparent rates with no hidden agency fees\n• Same-day cover when we have availability\n\nIf you'd like to know how we support homes like {{company_name}}, just reply to this email or ask for a quick 10-minute call — no obligation.\n\nWarm regards,\nOsabea Staffing Team`,
  sms: 'Hi, {{contact_name}} from Osabea Healthcare — we supply staff to care homes in Kent. Open to a quick call this week? Reply STOP to opt out.',
  whatsapp: `Hi {{contact_name}}, it's {{your_name}} from Osabea Healthcare Solutions. We help care homes with compliant agency staff (HCA, nurses, support workers). Would you have 5 minutes to chat about your cover needs?`,
  linkedin: 'Hi {{contact_name}}, I support care homes in Kent with compliant staffing cover. Would be glad to connect.',
  instagram: 'Hi — we supply compliant care staff to homes in Kent. Happy to share how we work if useful.',
  facebook: 'Hello — Osabea Healthcare supplies agency staff to care providers. Message us if you need cover.',
  video_call: 'Video intro — walked through Osabea compliance, rates, and how we fill shifts.',
  in_person: 'Visited the home — introduced Osabea staffing services to the manager.',
  letter: 'Posted intro letter with staffing overview and contact details.',
};

const SOURCE_LABELS = Object.fromEntries(LEAD_SOURCES.map((s) => [s.value, s.label]));
const CHANNEL_LABELS = Object.fromEntries(OUTREACH_CHANNELS.map((c) => [c.value, c.label]));
const OUTCOME_LABELS = Object.fromEntries(ACTIVITY_OUTCOMES.map((o) => [o.value, o.label]));

export function labelForSource(v) {
  return SOURCE_LABELS[v] || v || '—';
}

export function labelForChannel(v) {
  return CHANNEL_LABELS[v] || v || '—';
}

export function labelForOutcome(v) {
  return OUTCOME_LABELS[v] || v || '';
}

function digitsOnly(phone) {
  return (phone || '').replace(/\D/g, '');
}

function ukMobileWa(phone) {
  const d = digitsOnly(phone);
  if (d.startsWith('44')) return d;
  if (d.startsWith('0')) return `44${d.slice(1)}`;
  return d;
}

function searchQuery(lead) {
  const parts = [lead?.company_name, lead?.postcode].filter(Boolean);
  return encodeURIComponent(parts.join(' '));
}

function fillTemplate(text, lead) {
  return (text || '')
    .replace(/\{\{contact_name\}\}/g, lead?.contact_name || 'there')
    .replace(/\{\{company_name\}\}/g, lead?.company_name || 'your home')
    .replace(/\{\{your_name\}\}/g, 'Osabea');
}

export function buildResearchUrl(shortcut, lead) {
  const company = lead?.company_name || '';
  const q = searchQuery(lead);
  switch (shortcut) {
    case 'cqc':
      if (lead?.cqc_location_id) {
        return `https://www.cqc.org.uk/location/${lead.cqc_location_id}`;
      }
      return `https://www.cqc.org.uk/search/all?query=${encodeURIComponent(company)}`;
    case 'google':
      return `https://www.google.com/search?q=${q}`;
    case 'maps':
      return `https://www.google.com/maps/search/?api=1&query=${q}`;
    case 'website':
      if (lead?.website_url) {
        const url = lead.website_url.trim();
        return url.startsWith('http') ? url : `https://${url}`;
      }
      return `https://www.google.com/search?q=${encodeURIComponent(`${company} care home official website`)}`;
    case 'companies_house':
      return `https://find-and-update.company-information.service.gov.uk/search?q=${encodeURIComponent(company)}`;
    default:
      return null;
  }
}

export function buildOutreachUrl(channel, lead) {
  const phone = digitsOnly(lead?.contact_phone);
  const email = lead?.contact_email?.trim();
  const company = lead?.company_name || 'care home';
  const body = encodeURIComponent(fillTemplate(OUTREACH_TEMPLATES[channel] || '', lead));
  const subject = encodeURIComponent(`Staffing support for ${company}`);
  const mapsQ = searchQuery(lead);

  switch (channel) {
    case 'call':
      return phone ? `tel:${phone}` : null;
    case 'sms':
      return phone ? `sms:${phone}?body=${body}` : null;
    case 'whatsapp': {
      const wa = ukMobileWa(lead?.contact_phone);
      return wa ? `https://wa.me/${wa}?text=${body}` : null;
    }
    case 'email':
      return email ? `mailto:${email}?subject=${subject}&body=${body}` : `mailto:${PUBLIC_EMAIL}?subject=${subject}&body=${body}`;
    case 'linkedin':
      if (lead?.linkedin_url) return lead.linkedin_url.startsWith('http') ? lead.linkedin_url : `https://${lead.linkedin_url}`;
      return `https://www.linkedin.com/search/results/companies/?keywords=${encodeURIComponent(company)}`;
    case 'instagram': {
      const handle = (lead?.social_handle || '').replace('@', '').trim();
      return handle ? `https://www.instagram.com/${handle}/` : 'https://www.instagram.com/osabeacaresolutions';
    }
    case 'facebook':
      if (lead?.facebook_url) return lead.facebook_url.startsWith('http') ? lead.facebook_url : `https://${lead.facebook_url}`;
      return `https://www.facebook.com/search/top?q=${encodeURIComponent(company)}`;
    case 'video_call':
      return 'https://teams.microsoft.com/l/meeting/new';
    case 'in_person':
      return mapsQ ? `https://www.google.com/maps/search/?api=1&query=${mapsQ}` : null;
    default:
      return null;
  }
}

/** Which contact routes are ready vs missing — drives the coverage chips */
export function getLeadChannelCoverage(lead) {
  const has = (field) => Boolean((lead?.[field] || '').trim());
  return [
    { key: 'phone', label: 'Phone', ready: has('contact_phone') },
    { key: 'email', label: 'Email', ready: has('contact_email') },
    { key: 'whatsapp', label: 'WhatsApp', ready: has('contact_phone') },
    { key: 'linkedin', label: 'LinkedIn', ready: has('linkedin_url') },
    { key: 'instagram', label: 'Instagram', ready: has('social_handle') },
    { key: 'facebook', label: 'Facebook', ready: has('facebook_url') },
    { key: 'website', label: 'Website', ready: has('website_url') },
    { key: 'visit', label: 'Visit', ready: has('postcode') || has('address_line_1') },
  ];
}

export async function copyOutreachTemplate(channel, lead) {
  const text = fillTemplate(OUTREACH_TEMPLATES[channel] || '', lead);
  if (!text || !navigator?.clipboard?.writeText) return false;
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** Suggest next channel from playbook based on touch count */
export function suggestNextChannel(lead) {
  const attempts = Number(lead?.contact_attempts || 0);
  const step = OUTREACH_PLAYBOOK[attempts % OUTREACH_PLAYBOOK.length];
  if (!step) return OUTREACH_PLAYBOOK[0];
  const ch = step.channel;
  if ((ch === 'whatsapp' || ch === 'sms') && !lead?.contact_phone) {
    return OUTREACH_PLAYBOOK[(attempts + 1) % OUTREACH_PLAYBOOK.length];
  }
  if (ch === 'email' && !lead?.contact_email) {
    return OUTREACH_PLAYBOOK[(attempts + 1) % OUTREACH_PLAYBOOK.length];
  }
  if (ch === 'in_person' && !lead?.postcode && !lead?.address_line_1) {
    return OUTREACH_PLAYBOOK[(attempts + 1) % OUTREACH_PLAYBOOK.length];
  }
  return step;
}

export function defaultActivitySummary(channel, lead) {
  return fillTemplate(OUTREACH_TEMPLATES[channel] || `Outreach via ${labelForChannel(channel)}`, lead);
}

export function outcomeSuggestsFollowUpDays(outcome) {
  const map = {
    no_answer: 2,
    voicemail: 3,
    gatekeeper: 4,
    callback_requested: 1,
    interested: 2,
    email_sent: 3,
    message_sent: 2,
    connected: 5,
    meeting_booked: 1,
  };
  return map[outcome] ?? 3;
}

export function addDaysIso(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  d.setHours(10, 0, 0, 0);
  return d.toISOString();
}